import { FOOTER_CTA_TYPES } from '../global-constants';

/**
 * Parses the removeFooterLinks preference string into an array
 * @param {string} removeFooterLinks - Comma-separated string of links to remove
 * @returns {string[]} Array of link types to remove
 */
export const parseRemovedFooterLinks = (removeFooterLinks) => {
	return removeFooterLinks
		? removeFooterLinks.split(',').map((item) => item.trim())
		: [];
};

/**
 * Calculates the complete footer CTA visibility based on preferences and site properties
 * @param {string} removeFooterLinks - Comma-separated string of links to remove
 * @param {string} displaySaveIconOn - Whether save icon displays outside footer ('true' or 'false')
 * @param {boolean} enableMyCars - Whether MyCars feature is enabled
 * @param {string} enableCompare - Whether compare feature is enabled ('true' or 'false')
 * @returns {Object} Footer CTA visibility with helper functions and computed values
 */
export const calculateFooterCTAVisibility = (
	removeFooterLinks,
	displaySaveIconOn,
	enableMyCars,
	enableCompare
) => {
	const removedLinksArray = parseRemovedFooterLinks(removeFooterLinks);

	/**
	 * Checks if a specific footer CTA should be removed
	 * @param {string} ctaType - CTA type from FOOTER_CTA_TYPES
	 * @returns {boolean}
	 */
	const isCTARemoved = (ctaType) => removedLinksArray.includes(ctaType);

	// Save is considered "removed" from footer if it displays elsewhere OR is in removeFooterLinks
	const saveIsRemoved =
		displaySaveIconOn === 'true' || isCTARemoved(FOOTER_CTA_TYPES.SAVE);

	// MyCars footer section shows if MyCars is enabled AND either track OR save is still visible
	const footerHasMyCars =
		enableMyCars &&
		(!isCTARemoved(FOOTER_CTA_TYPES.TRACK) || !saveIsRemoved);

	// Both track and save should be removed if track is removed AND save is truly removed from everywhere
	const shouldRemoveBothMyCarsCTAs =
		isCTARemoved(FOOTER_CTA_TYPES.TRACK) &&
		isCTARemoved(FOOTER_CTA_TYPES.SAVE) &&
		displaySaveIconOn !== 'true';

	// Compare should show if enabled via preference AND not in removeFooterLinks
	const shouldShowCompare =
		enableCompare === 'true' && !isCTARemoved(FOOTER_CTA_TYPES.COMPARE);

	return {
		isCTARemoved,
		saveIsRemoved,
		footerHasMyCars,
		shouldRemoveBothMyCarsCTAs,
		shouldShowCompare
	};
};
